import Vue from 'vue'
import { Module, ActionContext } from 'vuex'
import isEqual from 'lodash.isequal'
import { checkWallAvailability, validateWall, Wall, WallBackgroundCollection } from '@padlet/arvo'
import { RootState } from 'surface/xstore/types'
import PadletApi from 'surface/padlet_api'
import { __ } from 'bits/intl'
import cloneDeep from 'lodash.clonedeep'
import merge from 'lodash.merge'

enum SettingsSubpanel {
  Main = 'main',
  MapStyle = 'mapstyle',
  Wallpaper = 'wallpaper',
  Icon = 'icon',
  Address = 'address',
  Reactions = 'reactions',
}

interface WallpaperPicker {
  title: string,
  backgrounds: WallBackgroundCollection,
  xWallpaperNames: boolean,
}

interface SettingsPanelState {
  activePanel: SettingsSubpanel | null,
  isSaving: boolean,
  previewAttributes: { [key: string]: any },
  saveErrorMessage: string | null,
  wallpaperPicker: WallpaperPicker | null,
  xCustomWallpaperPicker: boolean,
  xCustomIconPicker: boolean,
  isWallNameAvailable: boolean,
  isWallNameValid: boolean,
  isReactionDataValid: boolean,
  arePreviewAttributesValid: boolean,
  lastAvailableWallName: string | null,
}

type SettingsActionContext = ActionContext<SettingsPanelState, RootState>

const defaultState = (): SettingsPanelState => ({
  activePanel: null,
  isSaving: false,
  previewAttributes: {},
  saveErrorMessage: null,
  wallpaperPicker: null,
  xCustomWallpaperPicker: false,
  xCustomIconPicker: false,
  isWallNameAvailable: true,
  isWallNameValid: true,
  isReactionDataValid: true,
  arePreviewAttributesValid: true,
  lastAvailableWallName: null,
})

const SettingsModule: Module< SettingsPanelState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    arePreviewAttributesDirty (state, _, rootState): boolean {
      return !isEqual(state.previewAttributes, rootState.wall)
    },
    isOpen (state): boolean { return state.activePanel !== null },
    activePanel (state): SettingsSubpanel | null { return state.activePanel },
    previewAttributes (state): { [key: string]: any } { return state.previewAttributes },
    isSaving (state): boolean { return state.isSaving },
    isPreviewing (state): boolean { return Object.keys(state.previewAttributes).length > 0 },
    saveErrorMessage (state): string | null { return state.saveErrorMessage },
    wallpaperPicker (state): WallpaperPicker | null { return state.wallpaperPicker },
    xWallpaperPicker (state): boolean { return state.wallpaperPicker !== null },
    xCustomWallpaperPicker (state): boolean { return state.xCustomWallpaperPicker },
    xCustomIconPicker (state): boolean { return state.xCustomIconPicker },
  },
  mutations: {
    HIDE_SETTINGS_PANEL (state): void {
      Object.assign(state, defaultState())
    },
    SHOW_SETTINGS_PANEL (state, { activePanel, previewAttributes }: SettingsPanelState): void {
      state.activePanel = activePanel
      state.previewAttributes = previewAttributes
    },
    CHANGE_ACTIVE_PANEL (state, panel: SettingsSubpanel): void {
      state.activePanel = panel
    },
    SHOW_WALLPAPER_PICKER (state, options: WallpaperPicker): void {
      state.wallpaperPicker = options
    },
    HIDE_WALLPAPER_PICKER (state): void {
      state.wallpaperPicker = null
    },
    SHOW_CUSTOM_WALLPAPER_PICKER (state): void {
      state.xCustomWallpaperPicker = true
    },
    HIDE_CUSTOM_WALLPAPER_PICKER (state): void {
      state.xCustomWallpaperPicker = false
    },
    SHOW_CUSTOM_ICON_PICKER (state): void {
      state.xCustomIconPicker = true
    },
    HIDE_CUSTOM_ICON_PICKER (state): void {
      state.xCustomIconPicker = false
    },
    SETTINGS_SAVE (state): void {
      state.isSaving = true
      state.saveErrorMessage = null
    },
    SETTINGS_SAVE_SUCCESS (state): void {
      state.isSaving = false
    },
    SETTINGS_SAVE_ERROR (state, message): void {
      state.isSaving = false
      state.saveErrorMessage = message
    },
    UPDATE_PREVIEW_ATTRIBUTES (state, attributes): void {
      state.previewAttributes = merge(cloneDeep(state.previewAttributes), attributes)
    },
    UPDATE_WALL_AVAILABILITY (state, { isAvailable }: { isAvailable: boolean }): void {
      state.isWallNameAvailable = isAvailable
    },
    UPDATE_WALL_NAME_VALIDITY (state, { isValid }: { isValid: boolean }): void {
      state.isWallNameValid = isValid
    },
    UPDATE_WALL_REACTION_DATA_VALIDITY (state, { isValid }): void {
      state.isReactionDataValid = isValid
    },
    UPDATE_PREVIEW_ATTRIBUTES_VALIDITY (state, { arePreviewAttributesValid }): void {
      state.arePreviewAttributesValid = arePreviewAttributesValid
    },
    UPDATE_LAST_CHECKED_NAME (state: SettingsPanelState, name: string): void {
      state.lastAvailableWallName = name
    },
  },
  actions: {
    hideSettingsPanel ({ commit }): void {
      commit('HIDE_SETTINGS_PANEL')
    },
    showSettingsPanel ({ commit, rootState }, activePanel: SettingsSubpanel = SettingsSubpanel.Main): void {
      const previewAttributes = { ...rootState.wall }
      commit('SHOW_SETTINGS_PANEL', { activePanel, previewAttributes })
    },
    changeActivePanel ({ commit }, panel: SettingsSubpanel): void {
      commit('CHANGE_ACTIVE_PANEL', panel)
    },
    async saveSettings ({ commit, dispatch, state, rootGetters }): Promise<any> {
      try {
        commit('SETTINGS_SAVE')
        const updatedWall = await PadletApi.Wall.update(state.previewAttributes)
        dispatch('updateWall', updatedWall, { root: true })
        dispatch('updatePreviewAttributes', updatedWall)
        commit('SETTINGS_SAVE_SUCCESS')

        if (rootGetters['onboarding/isWallOnboardingFlow']) {
          dispatch('onboarding/goOnboardingNextStage', null, { root: true })
        }
      } catch {
        dispatch('setSaveSettingError', { message: __('Sorry! Something went wrong. Please try saving again.') })
      }
    },
    setSaveSettingError ({ commit }, { message }): void {
      commit('SETTINGS_SAVE_ERROR', message)
    },
    toggleSettingsPanel ({ dispatch, getters }, panel: SettingsSubpanel): void {
      if (getters.isOpen) {
        dispatch('hideSettingsPanel')
      } else {
        dispatch('showSettingsPanel', panel)
      }
    },
    updatePreviewAttributes ({ commit, getters, dispatch }: SettingsActionContext, attributes): void {
      commit('UPDATE_PREVIEW_ATTRIBUTES', attributes)
      if (getters.arePreviewAttributesDirty) {
        // next tick
        Vue.nextTick((): Promise<void> => dispatch('updateWallValidity'))
      }
    },
    showWallpaperPicker ({ commit }, options: WallpaperPicker): void {
      commit('SHOW_WALLPAPER_PICKER', options)
    },
    hideWallpaperPicker ({ commit }): void {
      commit('HIDE_WALLPAPER_PICKER')
    },
    updateBackground ({ commit, getters, rootState }, { url, fill, effect }): void {
      const reuseOriginalMetadata = url === rootState.wall.background.url
      const background = reuseOriginalMetadata
        ? { ...rootState.wall.background, fill, effect }
        : { ...getters.previewAttributes.background, url, fill, effect }
      commit('UPDATE_PREVIEW_ATTRIBUTES', { background })
    },
    updateBackgroundMetadata ({ commit }, { dominantColourAsRgb, imageLuminance }): void {
      const attributes = {
        background: {
          dominant_color_as_rgb: dominantColourAsRgb,
          luminance_name: imageLuminance,
        },
      }
      commit('UPDATE_PREVIEW_ATTRIBUTES', attributes)
    },
    pickCustomBackground  ({ commit, dispatch }, { url }): void {
      dispatch('updateBackground', { url, fill: 'cover' })
      commit('HIDE_CUSTOM_ICON_PICKER')
    },
    showCustomBackgroundPicker ({ dispatch, commit, rootGetters }): void {
      commit('SHOW_CUSTOM_WALLPAPER_PICKER')
      const payload = {
        onPickAction: 'settings/pickCustomBackground',
        initialSearchQuery: rootGetters.title,
        disallowGifSearch: true,
      }
      dispatch('imagePicker/showImagePickerSources', payload, { root: true })
    },
    hideCustomBackgroundPicker ({ dispatch, commit }): void {
      commit('HIDE_CUSTOM_WALLPAPER_PICKER')
      dispatch('imagePicker/hideImagePickerSources', null, { root: true })
    },
    pickCustomIconAndGoBack  ({ dispatch, commit }, { url }): void {
      dispatch('updatePreviewAttributes', { portrait: url || '' })
      dispatch('changeActivePanel', SettingsSubpanel.Main)
      commit('HIDE_CUSTOM_ICON_PICKER')
    },
    showCustomIconPicker ({ dispatch, commit, rootGetters }): void {
      commit('SHOW_CUSTOM_ICON_PICKER')
      const payload = {
        onPickAction: 'settings/pickCustomIconAndGoBack',
        initialSearchQuery: rootGetters.title,
      }
      dispatch('imagePicker/showImagePickerSources', payload, { root: true })
    },
    hideCustomIconPicker ({ dispatch, commit }): void {
      commit('HIDE_CUSTOM_ICON_PICKER')
      dispatch('imagePicker/hideImagePickerSources', null, { root: true })
    },
    async updateWallValidity ({ commit, state, rootState }: SettingsActionContext): Promise<void> {
      try {
        // reset validity
        commit('UPDATE_WALL_NAME_VALIDITY', { isValid: true })
        commit('UPDATE_WALL_REACTION_DATA_VALIDITY', { isValid: true })
        let arePreviewAttributesValid = true
        const id = Number(rootState.wall.id)
        const previewAttributes = state.previewAttributes as Wall
        await validateWall({ id, ...previewAttributes })

        // check if name is different and update wall name availability
        const { name } = state.previewAttributes
        if (name && name !== rootState.wall.name && name !== state.lastAvailableWallName) {
          // Check against last queried name so we don't make wasted queries against
          // a name that has already been validated in this round of settings updates.
          const isAvailable = await checkWallAvailability(name, rootState.wall.id)
          commit('UPDATE_WALL_AVAILABILITY', { isAvailable })
          commit('UPDATE_LAST_CHECKED_NAME', name)
          arePreviewAttributesValid = arePreviewAttributesValid && isAvailable
        }
        commit('UPDATE_PREVIEW_ATTRIBUTES_VALIDITY', { arePreviewAttributesValid })
      } catch (e) {
        const reasons = e.reasons || []
        const isWallNameInvalid = reasons.find(
          (reason: { field: string }): boolean => reason.field === 'name'
        )
        if (isWallNameInvalid) {
          commit('UPDATE_WALL_NAME_VALIDITY', { isValid: false })
        }
        const isReactionDataInvalid = reasons.find(
          (reason: { field: string }): boolean => reason.field === 'reaction_data'
        )
        if (isReactionDataInvalid) {
          commit('UPDATE_WALL_REACTION_DATA_VALIDITY', { isValid: false })
        }
        commit('UPDATE_PREVIEW_ATTRIBUTES_VALIDITY', { arePreviewAttributesValid: false })
      }
    },
  },
}
export default SettingsModule
export {
  SettingsSubpanel,
  SettingsPanelState,
}
